import React, { Fragment, useCallback, useMemo } from 'react';
import {
  STEP_SECTIONS,
  RFI_CREATE_STEPS,
  getDocumentsForInitialValues,
  getNotificationMediumFormValue,
  CUSTOM_NOTE_FIELD_NAME,
  NOTIFICATION_MEDIUM_FIELD_NAME,
} from '../pages/RfiCreate/utils';
import StepFieldWrapper from '../pages/RfiCreate/components/StepFieldWrapper';
import { useTranslation } from 'react-i18next';
import {
  DataRoomRfiTemplate,
  DATA_ROOM_RFI_CREATION_TYPE,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';

export default function useCreateRfi(
  isCreatingOrEditingTemplate: boolean,
  template?: DataRoomRfiTemplate,
) {
  const { t } = useTranslation();

  const initialValues = useMemo(
    () => ({
      creationType: DATA_ROOM_RFI_CREATION_TYPE.CHECKLIST,
      ...getDocumentsForInitialValues(template?.documents || []),
      ...(!isCreatingOrEditingTemplate && {
        recipients: [],
        twentyFourHoursBeforeDeadlineReminder: true,
        atDeadlineReminder: true,
        grantAccessDataRoomMemberIds: [],
      }),
      ...(template && {
        [CUSTOM_NOTE_FIELD_NAME]: template.customNote,
        [NOTIFICATION_MEDIUM_FIELD_NAME]: getNotificationMediumFormValue(
          template.notificationMedium,
        ),
      }),
      ...(template &&
        isCreatingOrEditingTemplate && { saveAsTemplateName: template.name }),
    }),
    [isCreatingOrEditingTemplate, template],
  );

  const visibleSteps = useMemo(
    () =>
      STEP_SECTIONS.filter(
        ({ hideForTemplate }) =>
          !hideForTemplate || !isCreatingOrEditingTemplate,
      ),
    [isCreatingOrEditingTemplate],
  );

  const stepsWithPage = useMemo(
    () =>
      visibleSteps.map(({ Step, title, page, hideForSpreadsheet }, index) => ({
        page,
        hideForSpreadsheet,
        node: (
          <Fragment key={index}>
            {index > 0 && <hr />}
            <StepFieldWrapper stepNumber={index + 1} title={t(title)}>
              <Step isCreatingOrEditingTemplate={isCreatingOrEditingTemplate} />
            </StepFieldWrapper>
          </Fragment>
        ),
      })),
    [isCreatingOrEditingTemplate, t, visibleSteps],
  );

  const getStepsForPage = useCallback(
    (current: number) => stepsWithPage.filter(({ page }) => page === current),
    [stepsWithPage],
  );

  const stepsByPage = useMemo<
    Record<
      number,
      Array<{ node: React.ReactNode; hideForSpreadsheet?: boolean }>
    >
  >(
    () => ({
      [RFI_CREATE_STEPS.DOCUMENTS]: getStepsForPage(RFI_CREATE_STEPS.DOCUMENTS),
      [RFI_CREATE_STEPS.DETAILS]: getStepsForPage(RFI_CREATE_STEPS.DETAILS),
    }),
    [getStepsForPage],
  );

  return { initialValues, stepsByPage };
}
